import { getIn } from 'formik';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import DatePicker from 'react-date-picker';
import styled from 'styled-components';
import { Calendar, ErrorIconRed } from '../../assets';
import { Colors } from '../../constants';
import { ShowIf } from '../ShowIf/ShowIf';
import { useTealium } from '../../tealium/useTealium';

const ParentDiv = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 2.5%;
  border: 1px solid ${props => props.error ? Colors.Primary4 : Colors.GreyScale5};
  margin-bottom: ${props => props.error ? '20px' : '0px'};
  background: ${Colors.GreyScale1};
  border-radius: 8px;
  width: 600px;
  height: 60px;
`;

const Label = styled.span`
  position: absolute;
  width: inherit;
  max-width: 100%;
  font-size: 16px;
  margin: 25px 0 0 0;
  color: ${Colors.GreyScale13};
  transform: ${props => (props.focussed ? 'translateY(-20px) scale(0.75)' : '')};
  transform-origin: ${props => (props.focussed ? 'top left' : '')};
  padding: ${props => (props.focussed ? '0 0 0 60px' : '0 0 0 30px')};
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4,0,0.2,1);
  transition-duration: 150ms;
`;

const StyledDatePicker = styled(DatePicker)`

  &.react-date-picker{
    width: 100%;
    height: 100%;
    background: transparent;
    outline: none;
    border: none;
    display: flex;
    position: relative;

    & > :nth-child(1){
      color: ${props => props.focussed ? 'black' : 'transparent'};           // to change the color of / / of DatePicker
      border-radius: 4px;
      width: 100%;
      height: 65px;
      border: 0;
      background-color: transparent;

      & > :nth-child(1){

        position: relative;
        bottom: -6px;
        input {
          background-color: transparent;
          outline: none;
        }
      }

      & > :nth-child(2){

        position: relative;
        bottom: -2px;
        padding: 20px 0;
        input {
          background-color: transparent;
          outline: none;
        }
        display: flex;
        width: 30px;
      }
    }
      /* Access the internal calendar */
      .react-calendar {
        .react-calendar__tile--now {
          background: #9e9e9e !important;
        }
        .react-calendar__tile--now:enabled:hover {
          background: #9e9e9e !important;
        }

        /* Out-of-range dates: greyed out and visibly non-selectable (FSL-19280 AC3).
           Interim tokens pending UX/Design sign-off on AC7. */
        .react-calendar__tile:disabled {
          background: ${Colors.GreyScale1};
          color: ${Colors.GreyScale5};
          cursor: not-allowed;
        }
        .react-calendar__tile:disabled:hover {
          background: ${Colors.GreyScale1};
          color: ${Colors.GreyScale5};
        }
        /* --now wins on specificity above, so restate it for a disabled today */
        .react-calendar__tile--now:disabled {
          background: ${Colors.GreyScale1} !important;
          color: ${Colors.GreyScale5};
        }
        .react-calendar__navigation button:disabled {
          background: ${Colors.GreyScale1};
          color: ${Colors.GreyScale5};
          cursor: not-allowed;
        }
    }
  }

`;

const HR = styled.hr`
  width: 95%;
  border-radius: 2px;
  margin: 0;
  height: 0.5px;
  background-color: ${props => props.error ? Colors.Primary4 : Colors.GreyScale13};
  position: absolute;
  top: 65px;
  border: 0;
  display: none;
`;

const SubMessage = styled.div`
  font-size: 0.75rem;
  line-height: 0.75rem;
  margin-top: 0.25rem;
  display: flex;
  column-gap: 0.25rem;
  color: ${Colors.Primary4};
  width: 100%;
`;

const stringFormat = 'yyyy-MM-dd';
const stringFormatForMonthYearOnly = 'yyyy-MM';

const SEGMENT_NAMES = ['day', 'month', 'year'];
const YEAR_LENGTH = 4;
const DEFAULT_SEGMENT_LENGTH = 2;
const NUMERIC_KEY = /^\d$/;
const INVALID_DATE_MESSAGE = 'Please capture a valid date';

const isValidDateBound = (value) => value instanceof Date && !isNaN(value.getTime());

/**
 * Reads react-date-picker's individual segment inputs straight from the DOM.
 *
 * There is no prop for this. DateInput only calls onChange once every segment is
 * filled *and* passes native validity, and the year input carries min/max
 * attributes derived from minDate/maxDate - so a manually typed out-of-range
 * date never reaches Formik at all and the field fails silently. Inspecting the
 * inputs is the only way to see what the user actually typed (FSL-19280 AC5).
 *
 * The month-year variant renders no day input, hence the filter.
 */
/**
 * Writes a segment the way typing does, so React registers it as a change.
 *
 * React remembers the last value it rendered on the input instance, and plain
 * assignment to `input.value` updates that record as a side effect - React then
 * concludes nothing changed and discards the event, which is why rewriting a
 * segment from a listener used to swallow the keystroke outright. The prototype
 * setter leaves React's record alone, exactly as a real keystroke does.
 */
const writeSegment = (input, value) => {
  const { set } = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

  set.call(input, value);
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

// How many digits a segment holds. The year has no meaningful upper bound when
// the field has no maxDate - react-date-picker allows six digits there - so it
// is always capped at four.
const getSegmentLength = (input) => (input.name === 'year'
  ? YEAR_LENGTH
  : (input.getAttribute('max') || '').length || DEFAULT_SEGMENT_LENGTH);

const readTypedSegments = (container) => SEGMENT_NAMES
  .map((name) => container.querySelector(`input[name="${name}"]`))
  .filter(Boolean)
  .map((input) => ({ name: input.name, value: input.value.trim() }));

/**
 * Describes what is wrong with a manually typed date, or null when it is usable.
 *
 * Returns `clearable` for a date the user demonstrably finished and got wrong.
 * Those are safe to act on; a half-read set of segments is not, so it only ever
 * produces a message.
 *
 * `settled` means focus has left the field. A date is incomplete for most of the
 * time the user is typing it, so complaining about that mid-entry would flash an
 * error on every keystroke of the year - that complaint waits until they leave.
 * An out-of-range date, on the other hand, is reported as soon as it is legible.
 *
 * Bounds are compared at the precision the field actually captures: a month-year
 * field must not be told its own current month is out of range just because the
 * maxDate happens to fall mid-month.
 */
const getTypedDateError = (segments, { minDate, maxDate, hasValue }, { settled }) => {

  // Nothing typed, or the user cleared the field - onChange already handled it.
  if (!segments.length || segments.every(({ value }) => !value)) {
    return null;
  }

  const yearSegment = segments.find(({ name }) => name === 'year');
  const isComplete = segments.every(({ value }) => value)
    && yearSegment?.value.length === YEAR_LENGTH;

  if (!isComplete) {
    // Reading the segments as half-filled is not evidence of a bad entry once
    // the picker has accepted a date: the user is mid-edit, or React has yet to
    // re-render the segments from the value it just took. Only a field holding
    // nothing can honestly be called incomplete.
    return settled && !hasValue ? { message: INVALID_DATE_MESSAGE, clearable: false } : null;
  }

  const typedParts = Object.fromEntries(segments.map(({ name, value }) => [name, Number(value)]));
  const hasDay = 'day' in typedParts;
  const unit = hasDay ? 'day' : 'month';
  const displayFormat = hasDay ? 'dd/MM/yyyy' : 'MM/yyyy';

  // Luxon rejects a date that does not exist, such as 31/02, outright. The Date
  // constructor would instead roll it over into the next month and accept it.
  const typed = DateTime.fromObject({
    year: typedParts.year,
    month: typedParts.month,
    day: hasDay ? typedParts.day : 1
  });

  if (!typed.isValid) {
    return { message: INVALID_DATE_MESSAGE, clearable: true };
  }

  if (minDate && typed.startOf(unit) < DateTime.fromJSDate(minDate).startOf(unit)) {
    return {
      message: `Date cannot be before ${DateTime.fromJSDate(minDate).toFormat(displayFormat)}`,
      clearable: true
    };
  }

  if (maxDate && typed.startOf(unit) > DateTime.fromJSDate(maxDate).startOf(unit)) {
    return {
      message: `Date cannot be after ${DateTime.fromJSDate(maxDate).toFormat(displayFormat)}`,
      clearable: true
    };
  }

  return null;
};

/**
 * Takes a single options object.
 *
 * This previously ended in a rest parameter (`...props`) while the call site
 * passed the props object as one positional argument, so `props` was an array
 * and `props.minDate` / `props.maxDate` were always undefined - the min/max
 * range never reached the calendar (FSL-19280).
 */
const renderDatePicker = ({ field, form, showOnlyMonthYear, isFocussed, track, events, ...props }) => {

  const setCalendarDate = (value, dateFormat) => {

    form.setFieldTouched(field.name, true);

    const formattedDate = value ? DateTime.fromJSDate(value).toFormat(dateFormat) : '';
    form.setFieldValue(field.name, formattedDate);
    form.validateForm(form.values);

    if (formattedDate) {
      track(events.formBuilder.fieldInteraction(window.location.pathname, field.name, formattedDate));
    }

  };

  const dateFormat = props?.dateFormat ? props.dateFormat : 'yyyy/MM/dd';

  // Already sanitised by NewDateSelector so the calendar and the typed-input
  // check below it are held to exactly the same bounds.
  const { minDate = null, maxDate = null } = props;

  if (showOnlyMonthYear) {
    return (
      <StyledDatePicker
        onChange={(value) => {
          setCalendarDate(value, stringFormatForMonthYearOnly);
        }}
        maxDetail={'year'}
        name={field.name}
        focussed={isFocussed || !!field.value}
        calendarIcon={
          <img src={Calendar} className="icon-calendar" alt='calendarIcon' />
        }
        clearIcon={null}
        yearPlaceholder={isFocussed ? '----' : ''}
        monthPlaceholder={isFocussed ? '--' : ''}
        format="yyyy/MM"
        value={field.value ? DateTime.fromFormat(field.value, stringFormatForMonthYearOnly).toJSDate() : field.value}
        onCalendarClose={() => {
          form.setFieldTouched(field.name, true);
          form.validateForm(form.values);
        }}
        minDate={minDate}
        maxDate={maxDate}
      />);
  }

  return (
    <StyledDatePicker
      name={field.name}
      focussed={isFocussed || !!field.value}
      disabled={false}
      calendarIcon={
        <img src={Calendar} className="icon-calendar" alt='calendarIcon' />
      }
      clearIcon={null}
      yearPlaceholder={isFocussed ? '----' : ''}
      monthPlaceholder={isFocussed ? '--' : ''}
      dayPlaceholder={isFocussed ? '--' : ''}
      format={dateFormat}
      value={field.value ? DateTime.fromFormat(field.value, stringFormat).toJSDate() : field.value}
      onChange={(value) => { setCalendarDate(value, stringFormat); }}
      onCalendarClose={() => {
        form.setFieldTouched(field.name, true);
        form.validateForm(form.values);
      }}
      minDate={minDate}
      maxDate={maxDate}
    />);
};

export const NewDateSelector = ({ field, form, showOnlyMonthYear = false, ...props }) => {
  const [isFocussed, setIsFocussed] = useState(false);
  const [typedError, setTypedError] = useState(null);
  const touched = useMemo(() => getIn(form?.touched, field.name), [form?.touched, field.name]);
  const error = useMemo(() => getIn(form?.errors, field.name), [form?.errors, field.name]);
  const { track, events } = useTealium();
  const containerRef = useRef(null);

  // react-date-picker treats an Invalid Date as a hard bound and disables the
  // whole calendar, so anything unparseable has to become null.
  const minDate = isValidDateBound(props.minDate) ? props.minDate : null;
  const maxDate = isValidDateBound(props.maxDate) ? props.maxDate : null;

  // The DOM listeners below read through this ref rather than closing over the
  // values, so they always see the current bounds without being torn down on
  // every render - minDate/maxDate arrive as freshly built Date objects.
  const latestRef = useRef(null);
  latestRef.current = { minDate, maxDate, form, fieldName: field.name, hasValue: !!field.value };

  // Outlives the listener effect, which is torn down whenever focus changes -
  // otherwise the check a blur just scheduled would be cancelled by the very
  // focus change that caused it.
  const pendingRef = useRef(new Set());

  useEffect(() => () => pendingRef.current.forEach(clearTimeout), []);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) { return undefined; }

    const revalidate = (settled) => {
      const result = getTypedDateError(readTypedSegments(container), latestRef.current, { settled });

      setTypedError(result?.message ?? null);
      return result;
    };

    /**
     * Runs the check after the keystroke has finished being delivered.
     *
     * The segments are controlled inputs owned by react-date-picker. These
     * listeners fire while the browser is still delivering the event, before the
     * picker has been told about it - so rendering from here re-applies the
     * picker's previous value to the input and wipes the character the user just
     * typed, which then only takes effect on a second press. Nothing here needs
     * to be synchronous, so it all waits until the keystroke has landed.
     */
    const defer = (fn) => {
      const pending = pendingRef.current;
      const id = setTimeout(() => {
        pending.delete(id);
        fn();
      }, 0);

      pending.add(id);
    };

    const onInput = () => defer(() => revalidate(false));

    /**
     * Applies the length and leading-zero rules before the digit lands.
     *
     * These used to be applied afterwards by rewriting input.value, which cost
     * the user the keystroke: React saw no change and never told the picker
     * about it, so the digit only took effect when it was pressed a second time.
     */
    const onKeyDown = (event) => {
      const input = event.target;

      if (!NUMERIC_KEY.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      // The picker renders the leading zero of a single-digit month or day as a
      // separate element, so a segment holding only "0" reads back as "00".
      if (!input.value && event.key === '0') {
        event.preventDefault();
        return;
      }

      // react-date-picker blocks a digit once the segment is full, and cannot
      // see the selection inside a number input to tell that the user meant to
      // overwrite it - so retyping a complete year silently did nothing. Start
      // a fresh entry instead of dropping the keystroke.
      if (input.value.length >= getSegmentLength(input)) {
        event.preventDefault();
        writeSegment(input, event.key);
      }
    };

    const onBlur = (event) => {
      // Blur also fires moving from the day segment to the month segment, where
      // a half-typed date is expected. Only judge the entry once focus has left
      // the field altogether.
      if (event.relatedTarget && container.contains(event.relatedTarget)) { return; }

      defer(() => {
        // Only a finished-but-wrong entry is clearable. Acting on a half-read
        // set of segments used to wipe a date the user had entered correctly,
        // which then surfaced the field's own "required" error.
        if (!revalidate(true)?.clearable) { return; }

        const { form: currentForm, fieldName } = latestRef.current;

        // The library swallowed this entry, so Formik still holds the previous
        // value while the inputs show something else. Clear it so the form
        // cannot be submitted with a date the user can no longer see. Passing
        // false skips validation, which would otherwise overwrite the message.
        currentForm.setFieldValue(fieldName, '');
        currentForm.setFieldTouched(fieldName, true, false);
      });
    };

    const segments = [...container.querySelectorAll(
      SEGMENT_NAMES.map((name) => `input[name="${name}"]`).join(', ')
    )];

    segments.forEach((input) => {
      input.addEventListener('keydown', onKeyDown);
      input.addEventListener('input', onInput);
      input.addEventListener('blur', onBlur);
    });

    return () => segments.forEach((input) => {
      input.removeEventListener('keydown', onKeyDown);
      input.removeEventListener('input', onInput);
      input.removeEventListener('blur', onBlur);
    });
  }, [isFocussed]);

  // A value landing means the entry was accepted - by the calendar or by typing
  // - so any message about what was typed before is stale. Guarded on a truthy
  // value because onBlur clears the field precisely when there is a message.
  useEffect(() => {
    if (field.value) { setTypedError(null); }
  }, [field.value]);

  // The typed-input message takes precedence: it is more specific than the
  // "required" error that clearing an out-of-range entry leaves behind.
  const displayError = typedError || (touched ? error : undefined);

  return (
    <ParentDiv
      ref={containerRef}
      onFocus={() => setIsFocussed(true)}
      onBlur={() => setIsFocussed(false)}
      error={displayError}
    >
      <Label
        focussed={isFocussed || !!field.value}
      >
        {props.label}
      </Label>

      {renderDatePicker({
        field, form, showOnlyMonthYear, isFocussed, track, events, ...props, minDate, maxDate
      })}

      <HR error={displayError} />

      <ShowIf condition={!!displayError}>
        <SubMessage>
          <img
            src={ErrorIconRed}
            alt="error"
          />
          {displayError}
        </SubMessage>
      </ShowIf>
    </ParentDiv>
  );
};

NewDateSelector.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object,
  label: PropTypes.string,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  showOnlyMonthYear: PropTypes.bool
};